import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import "../css/css_pages/Dashboard.css";
import "../css/css_layouts/Table.css";
import "../css/css_pages/Button.css";
import config from "../../config";
import { useNavigate } from 'react-router-dom';

// Helper function to format date as YYYY-MM-DD
const formatDate = (date) => {
  return date.toLocaleDateString("en-CA");
};

const formatCurrentMonthForInput = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
};

const CustomerServiceProgress = () => {
  const token = Cookies.get("authToken");
  const navigate = useNavigate();
  
  // Get default dates for the current month
  const [selectedMonth, setSelectedMonth] = useState(formatCurrentMonthForInput());

  // State to display the date range of the generated report
  const [reportDateRange, setReportDateRange] = useState({ start: '', end: '' });

  const [tsoDataList, setTsoDataList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 10;

  const [isDownloading, setIsDownloading] = useState(false);

  // A helper function to get the first and last day from a month string (e.g., "2023-11")
  const getMonthDateRange = (monthString) => {
    const [year, month] = monthString.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    return {
        firstDay: formatDate(firstDay),
        lastDay: formatDate(lastDay)
    };
  };

  const fetchTSOList = async (report_start_date_str, report_end_date_str) => {
    setIsLoading(true);
    setTsoDataList([]);
    try {
      const response = await fetch(
        `${config.API_URL}/management/customer-service-progress?report_start_date_str=${report_start_date_str}&report_end_date_str=${report_end_date_str}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      console.log("API Response:", data);
      if (Array.isArray(data)) {
        setTsoDataList(data);
        setReportDateRange({ start: report_start_date_str, end: report_end_date_str });
        setCurrentPage(1);
      } else {
        console.error("API response is not an array:", data);
      }
    } catch (error) {
      console.error("Error fetching TSO list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const { firstDay, lastDay } = getMonthDateRange(selectedMonth);
    fetchTSOList(firstDay, lastDay);
  }, []);

  const handleGenerateReport = () => {
    if (selectedMonth) {
      const { firstDay, lastDay } = getMonthDateRange(selectedMonth);
      fetchTSOList(firstDay, lastDay);
    } else {
      console.error("Please select both a start and end date.");
    }
  };
  
  const handleDownloadReport = async () => {
    if (!selectedMonth) {
        console.error("Please select a month before downloading.");
        return;
    }

    setIsDownloading(true);
    try {
      const { lastDay } = getMonthDateRange(selectedMonth);
      const response = await fetch(
        `${config.API_URL}/reports/create-tso-summary-report?report_start_date_str=${lastDay}&report_end_date_str=${lastDay}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to download report");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Extract filename from Content-Disposition if provided
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `tso_progress_report_${selectedMonth}.xlsx`;
      if (contentDisposition && contentDisposition.includes("filename=")) {
        filename = contentDisposition
          .split("filename=")[1]
          .replace(/['"]/g, "")
          .trim();
      }

      // Create a link element and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading TSO list:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const filteredData = tsoDataList.filter((tso) =>
    tso.tsoDetails?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <h1 style={{ textAlign: "center" }}>Customer Service Progress</h1>
      <button className="btn-primary" style={{ marginBottom: '10px' }} onClick={() => navigate("/service-progress-form")}>
          + Service Progress Form
      </button>
      
      <div className="report-controls" style={{ display: 'flex', justifyContent: 'left', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
        <div>
          <label htmlFor="month-select" style={{ marginRight: '5px' }}>Select Month:</label>
          <input
            type="month"
            id="month-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{ padding: "8px", borderRadius: "8px", border: "1px solid #ccc" }}
          />
        </div>
        <button onClick={handleGenerateReport} className="btn-primary" disabled={isLoading}>
          {isLoading ? 'Loading...' : 'Show Progress'}
        </button>
        <button onClick={handleDownloadReport} className="btn-primary" disabled={isDownloading}>
          {isDownloading ? 'Downloading...' : 'Download Progress Report'}
        </button>
      </div>

      {/* This heading will now correctly show the full month range of the report */}
      {reportDateRange.start && !isLoading && (
        <h4 style={{ textAlign: "left", color: '#555' }}>
          Showing progress of {selectedMonth}
        </h4>
      )}

      <input
        type="text"
        placeholder="Search by TSO Name"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{ marginBottom: "20px", padding: "10px", width: "300px", borderRadius: "10px", border: "2px solid gray" }}
      />

      <div className="management-table-wrapper">
        <div className="table-container">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th rowSpan="2">TSO Name</th>
                <th colSpan="3">Progress</th>
                <th colSpan="6">Service Details</th>
                <th colSpan="4">Feedback Counts</th>
                <th colSpan="2">Travel</th>
                <th colSpan="13">TSO Monthly Progress</th>
              </tr>
              <tr>
                <th>Service %</th>
                <th>Monthly Collection %</th>
                <th>Overall Collection %</th>
                {/* Service Details */}
                <th>Clients</th>
                <th>Total Service</th>
                <th>Visits</th>
                <th>Online</th>
                <th>Special Visits</th>
                <th>Reports Received</th>
                {/* Feedback Counts */}
                <th>Best</th>
                <th>Good</th>
                <th>Average</th>
                <th>Bad</th>
                {/* Travel */}
                <th>KM Travelled</th>
                <th>Bus Charges (Rs.)</th>
                {/* Monthly Progress */}
                <th>No of pending tasks</th>
                <th>Meeting attendance</th>
                <th>Training attendance</th>
                <th>Aptitude test</th>
                <th>Monthly service 30%</th>
                <th>Pending tasks</th>
                <th>Monthly collection 20%</th>
                <th>Overall collection 15%</th>
                <th>Customer feedback</th>
                <th>Meeting attendance 5%</th>
                <th>Training attendance 5%</th>
                <th>Aptitude test 5%</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="22" style={{ textAlign: "center" }}>Loading...</td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((tso, index) => {
                  const details = tso.tsoDetails || {};
                  const report = tso.tsoReportData || {};

                  return (
                    <tr key={details.id || index}>
                      <td>{details.name || "-"}</td>
                      {/* Progress */}
                      <td>{report.service_progress_percent ?? "-"}</td>
                      <td>{report.monthly_collection_percent ?? "-"}</td>
                      <td>{report.overall_collection_percent ?? "-"}</td>
                      {/* Service Details */}
                      <td>{report.client_count ?? "-"}</td>
                      <td>{report.total_service ?? "-"}</td>
                      <td>{report.visits ?? "-"}</td>
                      <td>{report.online ?? "-"}</td>
                      <td>{report.special_visits ?? "-"}</td>
                      <td>{report.report_receive_count ?? "-"}</td>
                      {/* Feedback Counts */}
                      <td>{report.best_count ?? "-"}</td>
                      <td>{report.good_count ?? "-"}</td>
                      <td>{report.average_count ?? "-"}</td>
                      <td>{report.bad_count ?? "-"}</td>
                      {/* Travel */}
                      <td>{report.km_travelled ?? "-"}</td>
                      <td>{report.bus_charges ?? "-"}</td>
                      {/* Monthly Progress */}
                      <td>{report.monthly_progress.no_of_pending_tasks ?? "-"}</td>
                      <td>{report.monthly_progress.meeting_attendance ?? "-"}</td>
                      <td>{report.monthly_progress.training_attendance ?? "-"}</td>
                      <td>{report.monthly_progress.aptitude_test ?? "-"}</td>
                      <td>{report.monthly_progress.monthly_service_30_percent ?? "-"}</td>
                      <td>{report.monthly_progress.pending_tasks_percent ?? "-"}</td>
                      <td>{report.monthly_progress.monthly_collection_20_percent ?? "-"}</td>
                      <td>{report.monthly_progress.overall_collection_15_percent ?? "-"}</td>
                      <td>{report.monthly_progress.customer_feedback_percent ?? "-"}</td>
                      <td>{report.monthly_progress.meeting_attendance_5_percent ?? "-"}</td>
                      <td>{report.monthly_progress.training_attendance_5_percent ?? "-"}</td>
                      <td>{report.monthly_progress.aptitude_test_5_percent ?? "-"}</td>
                      <td>{report.monthly_progress.total ?? "-"}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="22" style={{ textAlign: "center" }}>No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <br />

      <div className="pagination-buttons">
        {Array.from(
          { length: Math.ceil(filteredData.length / itemsPerPage) },
          (_, i) => (
            <button
              key={i}
              onClick={() => paginate(i + 1)}
              disabled={i + 1 === currentPage}
              className={i + 1 === currentPage ? "active" : "btn-primary"}
            >
              {i + 1}
            </button>
          )
        )}
      </div>
    </>
  );
};

export default CustomerServiceProgress;
